import { Router } from '@/routes';
import { Toaster } from '@/components/ui/sonner';
import { Sentry } from '@/lib/sentry';
import { DesktopOnlyGate } from '@/components/DesktopOnlyGate';

/**
 * Fallback UI rendered when a React error is caught by the Sentry ErrorBoundary.
 * Shows the error message and a retry button so the user never sees a blank screen.
 */
export function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  const message = error instanceof Error ? error.message : 'An unexpected error occurred';
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <h2 className="text-xl font-semibold text-red-600">Something went wrong</h2>
      <p className="max-w-md text-sm text-gray-600">{message}</p>
      <button
        onClick={resetError}
        className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
      >
        Try Again
      </button>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <DesktopOnlyGate>
        <Router />
        <Toaster />
      </DesktopOnlyGate>
    </Sentry.ErrorBoundary>
  );
}

export default App;
